import { useContext, useEffect, useState, useRef } from "react";

import MUIStack from "@mui/material/Stack";
import MUIBox from "@mui/material/Box";
import MUITypography from "@mui/material/Typography";
import MUILoadingButton from "@mui/lab/LoadingButton";
import MUIShortcutIcon from "@mui/icons-material/Shortcut";
import MUIDirectionsIcon from "@mui/icons-material/Directions";
import MUIAlert from "@mui/material/Alert";
import MUIMenuItem from "@mui/material/MenuItem";
import MUITextField from "@mui/material/TextField";
import MUIButton from "@mui/material/Button";
import MUIGrid from "@mui/material/Grid";
import MUIAutocomplete from "@mui/material/Autocomplete";
import MUIListItemIcon from "@mui/material/ListItemIcon";

import { ChartContext } from "../../../Context/ChartContext/ChartContext";
import MainContext from "../../../Context/MainContext/MainContext";
import Card from "../../Card";
import ChipQueueRoute from "../../Chip/ChipQueueRoute";
import ChipQueueSubType from "../../Chip/ChipQueueSubType";
import ChipQueueOrderStatusReasonDetail from "../../Chip/ChipQueueOrderStatusReasonDetail";
import OrderActivity from "../../../Utility/Crud/OrderActivity";
import EmptyState from "../../EmptyState";
import api from "../../../Seating/Security/api";
import Security from "../../../Utility/Security";
import Menu from "../../Menu";
import Calendar from "../../../Utility/Calendar";
import OrderStatusReasonDetail from "../../../Utility/Crud/OrderStatusReasonDetail";
import OrderStatusReason from "../../../Utility/Crud/OrderStatusReason";
import OrderStatus from "../../../Utility/Crud/OrderStatus";
import Patient from "../../../Utility/Crud/Patient";

/**
 * CardChartRouting displays available routing options for the selected order,
 * allows selection of a route and optional subType, and applies the route.
 */

const title = "Routing";
const icon = MUIShortcutIcon;
const className = "CardChartRouting";

export default function CardChartRouting(props) {
    const chartContext = useContext(ChartContext);
    const mainContext = useContext(MainContext);
    /**
     * Until we are moved entirely to the 'new experience' and can remove these triggers we need to track the ones we no longer use like this
     */
    const deprecatedRouteTriggers = ["toggleCreateNoteModal", "togglePreAuthModal", "createApprovalNote"];

    /**
     * Returns the current order object from chartContext.
     * @returns {Object} The order object.
     */
    const order = chartContext.originalChart?.orderActivity?.[chartContext.orderId] ??
        chartContext.chart?.orderActivity?.[chartContext.orderId];

    const orderStatusReasonId = order?.orderStatusReasonId ??
    chartContext?.chart?.orderActivity?.[chartContext.orderId]?.orderStatusReasonId ?? null;
    const routeFromOrderStatusReasonId = orderStatusReasonId;

    const currentActivityTypeId = Object.entries(mainContext.orderActivityTypesIndexed)
        .find(([, value]) => value.name === chartContext.chart.orderActivity[chartContext.orderId]?.orderType);

    /**
     * Groups available queue routes by their toOrderStatus.
     * @returns {Object} An object mapping orderStatusId to array of routes.
     */
    const availableRoutesGrouped = Object.values(mainContext.queueRoutesIndexed)
        .filter(route =>
            route.fromOrderStatusReasonId === routeFromOrderStatusReasonId &&
            route.orderActivityTypeId === parseInt(currentActivityTypeId)
        )
        .reduce((groups, route) => {
            const orderStatusReason = mainContext.orderStatusReasonsIndexed[route.toOrderStatusReasonId];

            if (!orderStatusReason) {
                console.error(
                    `Order status reason ${route.toOrderStatusReasonId} was not found for queue route.`,
                    route
                );
                return groups;
            }

            const orderStatusId = orderStatusReason.orderStatus;
            (groups[orderStatusId] ??= []).push(route);
            return groups;
        }, {});

    const [selectedRoute, setSelectedRoute] = useState(null);
    const [selectedSubType, setSelectedSubType] = useState(null);
    const [isApplyingRoute, setIsApplyingRoute] = useState(false);
    const [pendingSaveAfterRoute, setPendingSaveAfterRoute] = useState(false);
    const [functionToSendOnRoute, setFunctionToSendOnRoute] = useState(null);
    const functionToRunAfterRouteRef = useRef(null);

    // Manual route state
    const [manualRouteMode, setManualRouteMode] = useState(false);

    // State for order status reason detail
    const [selectedOrderStatusReasonDetailId, setSelectedOrderStatusReasonDetailId] = useState(null);
    const [selectedBusinessSupportRequestReasonDetailId, setSelectedBusinessSupportRequestReasonDetailId] = useState(null);
    const [businessSupportRequestNoteText, setBusinessSupportRequestNoteText] = useState("");
    const [businessSupportResolutionNoteText, setBusinessSupportResolutionNoteText] = useState("");
    const [selectedBusinessSupportResolutionTermId, setSelectedBusinessSupportResolutionTermId] = useState(null);

    const saveAfterRouteRef = useRef(false);

    /**
     * Switches back to normal routing view from manual route mode.
     * Resets manual route state and clears selected route/subtype.
     */
    const handleExitManualRoute = () => {
        setManualRouteMode(false);
        setSelectedRoute(null);
        setSelectedSubType(null);
    };

    const abortRouting = () => {
        setIsApplyingRoute(false);
        setPendingSaveAfterRoute(false);
        setManualRouteMode(false);
        setSelectedRoute(null);
        setSelectedSubType(null);
        setSelectedBusinessSupportRequestReasonDetailId(null);
        setBusinessSupportRequestNoteText("");
        setBusinessSupportResolutionNoteText("");
        setSelectedBusinessSupportResolutionTermId(null);
    };

    function getLatestBusinessSupportRequestForCurrentOrder() {
        const businessSupportRequestsForOrder = Object.values(chartContext.chart?.businessSupportRequest || {})
            .filter(request =>
                request &&
                request.orderActivityId === chartContext.orderId &&
                !request.deleted
            )
            .sort((a, b) => (b?.id || 0) - (a?.id || 0));

        return businessSupportRequestsForOrder[0] || null;
    }

    /**
     * Renders the main content of the card, including the routes grid.
     * @returns {JSX.Element}
     */
    const renderContent = () => {
        if (manualRouteMode) {
            return renderManualRouteInput();
        } else {
            return renderNormalRouteGrid();
        }
    };

    /**
     * Returns the action buttons for the card.
     * @returns {Array<JSX.Element>}
     */
    const getActions = () => {
        // Check if selectedRoute has any order status reason details
        const detailsForSelectedRoute = selectedRoute
            ? manualRouteMode
                ? getOrderStatusReasonDetailsForReasonId(selectedRoute.id)
                : getOrderStatusReasonDetailsForRoute(selectedRoute)
            : [];

        const detailsRequired = detailsForSelectedRoute.length > 0;
        const isBusinessSupportRouteSelected = selectedRoute?.id === "request-business-support-help";
        const businessSupportNoteRequired = isBusinessSupportRouteSelected && businessSupportRequestNoteText.trim() === "";
        const isInBusinessSupportRequest = orderStatusReasonId === 116;
        const businessSupportRequestReasonRequired =
            !manualRouteMode && isInBusinessSupportRequest && !!selectedRoute && !selectedBusinessSupportRequestReasonDetailId;
        const businessSupportResolutionNoteRequired =
            !manualRouteMode && isInBusinessSupportRequest && !!selectedRoute && businessSupportResolutionNoteText.trim() === "";
        const businessSupportResolutionTermRequired =
            !manualRouteMode && isInBusinessSupportRequest && !!selectedRoute && !selectedBusinessSupportResolutionTermId;

        if (chartContext.pageMode) {
            return null
        }

        return [
            <MUIBox sx={{ key: "actions", display: 'flex', alignItems: 'center', gap: 1 }}>
                {manualRouteMode && (
                    <MUIButton
                        key="exit-manual"
                        size="small"
                        variant="text"
                        color="error"
                        onClick={handleExitManualRoute}
                    >
                        Cancel
                    </MUIButton>
                )}
                <MUILoadingButton
                    key="route"
                    loading={isApplyingRoute}
                    variant="contained"
                    disabled={
                        chartContext?.isUpdating ||
                        !selectedRoute ||
                        (selectedRoute?.jsonSubTypes?.required && !selectedSubType) ||
                        (detailsRequired && !selectedOrderStatusReasonDetailId) ||
                        businessSupportNoteRequired ||
                        businessSupportRequestReasonRequired ||
                        businessSupportResolutionNoteRequired ||
                        businessSupportResolutionTermRequired
                    }
                    endIcon={<MUIShortcutIcon />}
                    onClick={handleApplyRoute}
                    size="small"
                >
                    Route
                </MUILoadingButton>
                {Security.hasPermission("order.manualRoute") ? (
                    <Menu
                        menuItems={[
                            <MUIMenuItem
                                key="manual-route"
                                onClick={() => {
                                    setManualRouteMode(true);
                                    setSelectedRoute(null);
                                    setSelectedSubType(null);
                                }}
                            >
                                <MUIListItemIcon>
                                    <MUIDirectionsIcon fontSize="small" />
                                </MUIListItemIcon>
                                Manual Route
                            </MUIMenuItem>
                        ]}
                    />
                ) : null}
            </MUIBox>
        ]
    };

    /**
     * Renders subType options for the selected route, if present.
     * @param {Object} route - The selected route object.
     * @returns {JSX.Element|null} SubType chips or null.
     */
    const renderSubTypeOptions = (route) => {
        if (!route?.jsonSubTypes || !selectedRoute || selectedRoute.id !== route.id) {
            return null;
        }

        const required = !!route.jsonSubTypes.required;
        const options = route.jsonSubTypes.options || [];
        if (options.length === 0) {
            return null;
        }
        return (
            <MUIBox sx={{ flexGrow: 1, width: "100%" }}>
                <MUITypography variant="subtitle2">Subtype{required ? "*" : " (Optional)"}</MUITypography>
                <MUIGrid container spacing={2}>
                    {options.map((option) => (
                        <MUIGrid
                            item
                            key={option}
                            {...getGridItemProps()}
                        >
                            <ChipQueueSubType
                                queueRoute={route}
                                label={option}
                                value={option}
                                selectable={true}
                                clickable={true}
                                fullWidth={true}
                                selected={selectedSubType === option}
                                onClick={() => {
                                    setSelectedSubType(selectedSubType === option ? null : option);
                                }}
                            />
                        </MUIGrid>
                    ))}
                </MUIGrid>
                {props?.requiredValidation &&
                    chartContext.createTicketValidationIssues.routing ? (
                    <MUITypography variant="caption" color="error" sx={{ display: "block", mt: 2 }}>Required</MUITypography>
                ) : null}
            </MUIBox>
        );
    };

    function renderBusinessSupportRequestInput(route) {
        if (!route || !selectedRoute || selectedRoute.id !== route.id || route.id !== "request-business-support-help") {
            return null;
        }

        return (
            <MUIBox sx={{ flexGrow: 1, width: "100%" }}>
                <MUITextField
                    label="Describe the question/issue"
                    variant="standard"
                    InputLabelProps={{ shrink: true }}
                    size="small"
                    fullWidth
                    required
                    multiline
                    minRows={2}
                    maxRows={6}
                    value={businessSupportRequestNoteText}
                    onChange={(e) => setBusinessSupportRequestNoteText(e.target.value)}
                />
            </MUIBox>
        );
    }

    function renderBusinessSupportResolutionInputs(route) {
        if (!route || !selectedRoute || selectedRoute.id !== route.id || orderStatusReasonId !== 116) {
            return null;
        }

        const businessSupportResolutionTerms = Object.values(mainContext.termsIndexed || {})
            .filter(term =>
                term.type === "businesss_support_request_resolution" ||
                term.type === "business_support_request_resolution"
            )
            .sort((a, b) => (a.name || "").localeCompare(b.name || ""));
        const businessSupportRequestReasonDetails = getOrderStatusReasonDetailsForReasonId(116);
        const selectedRequestReasonDetail = businessSupportRequestReasonDetails.find(
            detail => detail.id === selectedBusinessSupportRequestReasonDetailId
        ) ?? null;
        const selectedRequestReasonLabel = (OrderStatusReasonDetail.getDisplayName(selectedRequestReasonDetail) || "").trim();
        const selectedRequestReasonText = (OrderStatusReasonDetail.getDisplayName(selectedRequestReasonDetail) || "")
            .trim()
            .toLowerCase();
        const filteredBusinessSupportResolutionTerms = businessSupportResolutionTerms.filter(term => {
            if (!selectedRequestReasonText) {
                return true;
            }
            return (term?.name || "").trim().toLowerCase().startsWith(selectedRequestReasonText);
        });
        const getResolutionOptionLabel = (term) => {
            const fullName = (term?.name || "").trim();
            if (!selectedRequestReasonLabel || !fullName.toLowerCase().startsWith(selectedRequestReasonText)) {
                return fullName;
            }

            const trimmedLabel = fullName.slice(selectedRequestReasonLabel.length).trimStart();
            return (trimmedLabel.replace(/^[-:|]\s*/, "") || fullName);
        };

        return (
            <MUIGrid container spacing={2}>
                <MUIGrid item xs={12} md={6}>
                    <MUIAutocomplete
                        options={businessSupportRequestReasonDetails}
                        value={
                            businessSupportRequestReasonDetails.find(
                                detail => detail.id === selectedBusinessSupportRequestReasonDetailId
                            ) ?? null
                        }
                        getOptionLabel={(detail) =>
                            OrderStatusReasonDetail.getDisplayName(detail)
                        }
                        onChange={(_, newValue) => {
                            setSelectedBusinessSupportRequestReasonDetailId(newValue?.id ?? null);
                        }}
                        renderInput={(params) => (
                            <MUITextField
                                {...params}
                                label="Request Reason"
                                variant="standard"
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                fullWidth
                                required
                            />
                        )}
                        isOptionEqualToValue={(option, value) => {
                            return option.id === value?.id;
                        }}
                    />
                </MUIGrid>
                <MUIGrid item xs={12} md={6}>
                    <MUIAutocomplete
                        options={filteredBusinessSupportResolutionTerms}
                        value={filteredBusinessSupportResolutionTerms.find(term => term.id === selectedBusinessSupportResolutionTermId) ?? null}
                        getOptionLabel={getResolutionOptionLabel}
                        onChange={(_, newValue) => {
                            setSelectedBusinessSupportResolutionTermId(newValue?.id ?? null);
                        }}
                        renderInput={(params) => (
                            <MUITextField
                                {...params}
                                label="Resolution"
                                variant="standard"
                                InputLabelProps={{ shrink: true }}
                                size="small"
                                fullWidth
                                required
                            />
                        )}
                        isOptionEqualToValue={(option, value) => {
                            return option.id === value?.id;
                        }}
                    />
                </MUIGrid>
                <MUIGrid item xs={12}>
                    <MUITextField
                        label="Resolution Note"
                        variant="standard"
                        InputLabelProps={{ shrink: true }}
                        size="small"
                        fullWidth
                        required
                        multiline
                        minRows={2}
                        maxRows={6}
                        value={businessSupportResolutionNoteText}
                        onChange={(e) => setBusinessSupportResolutionNoteText(e.target.value)}
                    />
                </MUIGrid>
            </MUIGrid>
        );
    }

    // Find trigger description for the selected route
    const triggerDescription = (() => {
        if (!selectedRoute || !selectedRoute.trigger) {
            return null;
        }
        const triggers = mainContext.queueRouteTriggers || [];
        const found = triggers.find(trigger => trigger.value === selectedRoute.trigger);
        return found ? found.description : null;
    })();

    // Whenever the current order reason changes, reset routing UI state so
    // stale selections/loading do not carry over to a different route context.
    useEffect(() => {
        abortRouting();
    }, [orderStatusReasonId]);

    // Reset detail selection when the selected route changes to avoid stale details.
    useEffect(() => {
        setSelectedOrderStatusReasonDetailId(null);
    }, [selectedRoute]);

    useEffect(() => {
        if (selectedRoute?.id !== "request-business-support-help") {
            setBusinessSupportRequestNoteText("");
        }
    }, [selectedRoute]);

    useEffect(() => {
        if (orderStatusReasonId !== 116) {
            setSelectedBusinessSupportRequestReasonDetailId(null);
            setBusinessSupportResolutionNoteText("");
            setSelectedBusinessSupportResolutionTermId(null);
            return;
        }

        const latestBusinessSupportRequest = getLatestBusinessSupportRequestForCurrentOrder();
        setSelectedBusinessSupportRequestReasonDetailId(
            latestBusinessSupportRequest?.requestOrderStatusReasonDetailId ?? null
        );
    }, [orderStatusReasonId, chartContext.chart?.businessSupportRequest, chartContext.orderId]);

    useEffect(() => {
        if (!selectedBusinessSupportRequestReasonDetailId) {
            setSelectedBusinessSupportResolutionTermId(null);
            return;
        }

        const requestReasonDetail = getOrderStatusReasonDetailsForReasonId(116)
            .find(detail => detail.id === selectedBusinessSupportRequestReasonDetailId);
        const requestReasonText = (OrderStatusReasonDetail.getDisplayName(requestReasonDetail) || "").trim().toLowerCase();

        if (!requestReasonText) {
            setSelectedBusinessSupportResolutionTermId(null);
            return;
        }

        const selectedResolutionTerm = Object.values(mainContext.termsIndexed || {})
            .find(term => term.id === selectedBusinessSupportResolutionTermId);
        const selectedResolutionName = (selectedResolutionTerm?.name || "").trim().toLowerCase();

        if (!selectedResolutionName.startsWith(requestReasonText)) {
            setSelectedBusinessSupportResolutionTermId(null);
        }
    }, [selectedBusinessSupportRequestReasonDetailId, selectedBusinessSupportResolutionTermId, mainContext.termsIndexed]);

    /**
     * Triggers adding a part and authorization if required.
     * @returns {Promise<Object>} Update object for routing.
     */
    async function triggerAddPartAndAuthorization() {
        if (checkForAddPart()) {
            setFunctionToSendOnRoute(() => postUpdateAddPartForDeliveryAndAuthorize);
            const partUpdate = await addPartForDeliveryAndAuthorize();
            return partUpdate;
        }
        return {};
    }

    /**
     * Triggers adding an approved part without approving the order.
     * @returns {Object} Update object for routing.
     */
    function triggerAddApprovedPart() {
        if (checkForAddPart()) {
            setFunctionToSendOnRoute(() => postUpdateAddApprovedPart);
            return buildAddPartRouteUpdate();
        }
        return {};
    }

    /**
     * Calculates and returns a follow-up date based on route and order context.
     * Uses business days or calendar days as appropriate.
     * @returns {Promise<Object>} Update object with followUpDate (ISO string).
     */
    async function triggerAddFollowupDate() {
        let useBusinessDays = true;
        let daysToAdd = 5;

        if (selectedRoute?.toOrderStatusReasonId === 57) { // Service - Documentation Followup
            daysToAdd = 3;
        } else if (
            selectedRoute?.toOrderStatusReasonId === 96 || // Business Support: Followup
            selectedRoute?.toOrderStatusReasonId === 112 // UHC Denied Appeals - Medicare
        ) {
            daysToAdd = 5;
        } else if (
            (selectedRoute?.toOrderStatusReasonId === 89 && order?.insuranceSubTypeId === 196) ||
            (selectedRoute?.toOrderStatusReasonId === 110)
        ) {
            // Preauth Followup - Appeals && UHC MCR Replacement ||
            // UHC Denied Appeals - Maximus
            //
            // Historically we used the PA F/U Appeals + UHC check for this.
            // That was swapped out to just use the new queue but keeping both
            // around for now.
            useBusinessDays = false;
            daysToAdd = 10;
        }

        return {
            followUpDate: Calendar.addDays({
                daysToAdd: daysToAdd,
                useBusinessDays: useBusinessDays
            })
        };
    }

    /**
     * Handles setting redelivery flags for the order.
     * @returns {Promise<Object>} Update object for redelivery.
     */
    async function triggerHandleNeedsRedelivery() {
        // Return update object for batching
        return {
            needsRedelivery: true,
            scheduledDeliveryDate: null,
            setupDate: null,
        };
    }

    /**
     * Triggers adding an "Unable to Pickup" note.
     * @returns {Promise<string>} Returns "continue" to proceed.
     */
    async function triggerAddUnableToPickupNote() {
        setFunctionToSendOnRoute(() => postUpdateAddUnableToPickupNote);
        return "continue";
    }

    /**
     * After save completes, navigate to the Service tab for this order
     * @returns {Promise<string>} Returns "continue" to proceed.
     */
    async function triggerCreatePickup() {
        const parentOrder = OrderActivity.getParentOrder(
            order.id,
            chartContext.chart
        );

        functionToRunAfterRouteRef.current = () => {
            chartContext.prepareTicketData(parentOrder, "pickup", {
                orderStatusReasonId: 121,
            });
        };

        return "continue";
    }

    /**
     * Routes back to sender using audit log to find previous status.
     * @returns {Promise<Object>} Update object for previous status/reason.
     */
    async function triggerRouteBackToSender() {
        // Implements legacy routeBackToSender logic, now using auditLog for previous order_status_reason_id
        const reasons = mainContext.orderStatusReasonsIndexed;
        const auditLog = chartContext.chart?.auditLog || {};
        const orderId = chartContext.orderId;
        const originalOrder = chartContext.chart?.orderActivity?.[orderId];
        let previousReasonId = null;
        let previousStatusId = null;
        // Find the most recent valid auditLog entry for this orderId and columnName 'order_status_reason_id'
        const auditEntries = Object.values(auditLog)
            .filter(entry => entry.tableName === "order_activity" && entry.columnName === "order_status_reason_id" && entry.rowId === orderId)
            .sort((a, b) => new Date(b.timestamp) - new Date(a.timestamp));
        let lastValidEntry = null;
        if (auditEntries.length > 0) {
            for (let i = 0; i < auditEntries.length; i++) {
                const reasonId = parseInt(auditEntries[i].oldValue);
                const reasonObj = reasons[reasonId];
                // Skip if reason is cancelled or matches current order reason
                if (
                    reasonId !== originalOrder?.orderStatusReasonId &&
                    reasonObj &&
                    reasonObj.description &&
                    !reasonObj.description.toLowerCase().includes("cancel")
                ) {
                    lastValidEntry = auditEntries[i];
                    break;
                }
            }
        }
        if (lastValidEntry) {
            previousReasonId = parseInt(lastValidEntry.oldValue);
            if (reasons[previousReasonId]) {
                previousStatusId = reasons[previousReasonId].orderStatus;
            }
        }
        return {
            status: previousStatusId,
            orderStatusReasonId: previousReasonId
        };
    }

    /**
     * Routes eligible Service orders to Non-billable Approved.
     *
     * Latest specs stamped by Kenny on 2026-08-17
     *
     * Eligible orderType
     * 	orderType = "Service"
     *
     * Delivery conditions
     * The eligible order must also meet at least one of these conditions:
     * 	No items were delivered.
     * 	Exactly one item exists, and its description contains:
     * 		30 Day Fit 4 U Adjustment,
     * 		ATP Service Request,
     * 		Technician Skilled Labor, or
     * 		Field Service Diagnosis.
     * 	One or more items were delivered, and every delivered item has HCPCS code NB.
     * 	MSRP = $0
     *
     * @returns {Promise<Object|string>} Non-billable Approved updates or "continue".
     */
    async function triggerCheckForNonBillable() {
        // Only Service orders are eligible for the Non-billable Approved override.
        const orderId = chartContext.orderId;
        const originalOrder = chartContext.chart.orderActivity[orderId];
        if (originalOrder.orderType !== "Service") {
            return "continue";
        }

        // Load the order's products and service parts from its latest service quote.
        const orderProducts = OrderActivity.getProducts(originalOrder, chartContext.chart).filter(Boolean);
        const serviceQuoteEntries = Object.values(chartContext.chart.serviceQuote)
            .filter(quote => quote.orderId === orderId);
        const latestServiceQuote = serviceQuoteEntries.reduce(
            (latest, current) => (!latest || current.id > latest.id ? current : latest),
            null
        );
        let serviceParts = [];
        if (latestServiceQuote?.documentData) {
            serviceParts = JSON.parse(latestServiceQuote.documentData).map(item => ({
                ...chartContext.chart.servicePartCatalog[item.ItemId],
                quantity: item.Qty
            }));
        }

        // Load the service items from the latest Service Ticket document.
        let latestDeliveryInformation = null;
        let deliveryServiceItems = [];
        for (const deliveryInformation of Object.values(chartContext.chart.deliveryInformation || {})) {
            if (
                deliveryInformation.orderId !== orderId ||
                deliveryInformation.documentType !== "Service Ticket"
            ) {
                continue;
            }

            let deliveryDocument;
            try {
                deliveryDocument = JSON.parse(deliveryInformation.documentInfoPruned);
            } catch {
                continue;
            }

            if (!deliveryDocument || !Array.isArray(deliveryDocument.serviceItems)) {
                continue;
            }

            if (
                !latestDeliveryInformation ||
                new Date(deliveryInformation.uploadedOn) > new Date(latestDeliveryInformation.uploadedOn)
            ) {
                latestDeliveryInformation = deliveryInformation;
                deliveryServiceItems = deliveryDocument.serviceItems;
            }
        }

        const billableItems = [];

        // Add each billable service part after skipping its non-billable conditions.
        for (const servicePart of serviceParts) {
            // Service parts are undelivered only when the delivery flag is explicitly false.
            if (
                !deliveryServiceItems.some(deliveryServiceItem =>
                    deliveryServiceItem.description?.trim().toUpperCase() === servicePart.description?.trim().toUpperCase() &&
                    deliveryServiceItem.hcpcs?.trim().toUpperCase() === servicePart.hcpc?.trim().toUpperCase() &&
                    deliveryServiceItem.delivered !== false
                )
            ) {
                continue;
            }

            // Service parts with HCPCS NB are not billable.
            if (servicePart.hcpc?.trim().toUpperCase() === "NB") {
                continue;
            }

            // A service part with $0 MSRP is not billable.
            if (Number(servicePart.msrp) === 0) {
                continue;
            }

            billableItems.push(servicePart);
        }

        // Define product subtypes that are always non-billable.
        const nonBillableApprovedProductSubTypeIds = [
            70, // 30 Day Fit 4 U Adjustment
            1041, // ATP Service Request
            1963, // Tech Skilled Labor
            1964 // Field Service Diagnosis
        ];

        // Add each billable product after skipping its non-billable conditions.
        for (const product of orderProducts) {
            // The listed service products are not billable.
            if (nonBillableApprovedProductSubTypeIds.includes(product.productSubTypeId)) {
                continue;
            }

            // Products with HCPCS NB are not billable.
            if (product.hcpcs?.trim().toUpperCase() === "NB") {
                continue;
            }

            // A product with $0 MSRP is not billable.
            if (Number(product.msrp) === 0) {
                continue;
            }

            billableItems.push(product);
        }

        // Keep the selected route when either loop finds a billable item.
        if (billableItems.length > 0) {
            return "continue";
        }

        // Route to Non-billable Approved when neither loop finds a billable item.
        return {
            status: 3, // Setup
            orderStatusReasonId: 56 // Non-billable Approved
        };
    }

    /**
     * Checks if the selected subType requires adding a part.
     * @returns {string|null} Part type string or null.
     */
    function checkForAddPart() {
        if (!selectedSubType) {
            return null;
        }
        let subType = String(selectedSubType).replace(/\s/g, '').toLowerCase();
        if (subType.includes("atp30dayfit4u")) {
            return "atp30day";
        } else if (subType.includes("atpservicerequest")) {
            return "atpService";
        } else if (subType.includes("30dayfit4u")) {
            return "30day";
        } else if (subType.includes("fieldservicediagnosis")) {
            return "fieldService";
        } else if (subType.includes("techskilledlabor")) {
            return "techSkilled";
        } else {
            return null;
        }
    }

    /**
     * Builds a product item for OrderService.updateOrderProducts.
     * @param {string} partType - Type of part.
     * @param {number|string} orderId - Order ID.
     * @param {Object} currentUser - Current user object.
     * @returns {Object} Product item object.
     */
    function buildPartProductItem(partType, orderId, currentUser) {
        let id = null;
        if (partType === "techSkilled") {
            id = 1963;
        } else if (partType === "30day") {
            id = 70;
        } else if (partType === "fieldService") {
            id = 1964;
        } else if (partType === "atp30day") {
            id = 2156;
        } else if (partType === "atpService") {
            id = 1041;
        }
        return {
            abnRequired: false,
            amount: 0,
            approved: true,
            hcpcs: partType ? "na" : "NB",
            isRental: false,
            orderActivityProducts: { orderActivityId: orderId },
            points: 0,
            productSubTypeId: id,
            productTypeId: 27,
            qty: 1,
            serial: "",
            updatedById: currentUser.id,
            updatedOn: new Date().toISOString()
        };
    }

    /**
     * Adds a part for delivery and authorization, and returns update object.
     * @returns {Promise<Object>} Update object for routing.
     */
    function buildAddPartRouteUpdate() {
        if (!selectedRoute) {
            return {};
        }
        const selectedOrderStatusReasonId = selectedRoute.toOrderStatusReasonId;
        const status = mainContext.orderStatusReasonsIndexed[selectedOrderStatusReasonId].orderStatus;

        // Calculate total points for products in this order
        const orderActivityProducts = chartContext.chart?.orderActivityProducts;
        const productData = chartContext.chart?.product;
        const orderId = chartContext.orderId;
        const orderProducts = orderActivityProducts
            ? Object.values(orderActivityProducts)
                .filter(orderActivityProduct => orderActivityProduct?.orderActivityId === orderId)
                .map(orderActivityProduct => productData?.[orderActivityProduct.productsId])
                .filter(Boolean)
            : [];
        const totalPoints = orderProducts.reduce((sum, product) => sum + (product.points || 0), 0);

        // Return update object for batching
        return {
            orderStatusReasonId: selectedOrderStatusReasonId,
            status,
            ...(selectedSubType ? { subType: selectedSubType } : {}),
            points: totalPoints,
        };
    }

    async function addPartForDeliveryAndAuthorize() {
        const partUpdate = buildAddPartRouteUpdate();
        const currentOrder = chartContext.chart?.orderActivity?.[chartContext.orderId];
        return {
            ...partUpdate,
            isApproved: true,
            ...(!currentOrder?.approvalDate ? { approvalDate: new Date().toISOString() } : {}),
        };
    }

    /**
     * Post-save function to add an approved part without approving the order.
     * @returns {Promise<void>}
     */
    async function postUpdateAddApprovedPart() {
        const partType = checkForAddPart();
        const newProduct = buildPartProductItem(partType, chartContext.orderId, mainContext.currentUser);
        await api.send("PUT", "/orders/updateOrderProducts/" + chartContext.orderId, [newProduct]);
    }

    /**
     * Post-save function for 'Unable to Pickup' note.
     * @returns {Promise<void>}
     */
    async function postUpdateAddUnableToPickupNote() {
        const orderId = chartContext.orderId;
        const orderObj = chartContext.chart?.orderActivity?.[orderId] ?? {};
        const accountEntry = chartContext.chart?.account ? Object.values(chartContext.chart.account)[0] : null;
        const noteText = `${mainContext.currentUser.firstname} ${mainContext.currentUser.lastname} marked order ${orderId} as unable to pick up on ${new Date().toLocaleString()}`;
        const note = {
            type: "ActivityNote",
            text: noteText,
            createdAt: new Date().toISOString(),
            accountId: accountEntry?.id,
            task: false,
            completed: true,
            createdById: mainContext.currentUser.id,
            patientName: orderObj?.patientName,
            activityId: orderObj?.id,
        };
        try {
            await api.send("POST", "/activities/" + orderObj?.id + "/notes", note);
        } catch (err) {
            console.error("Error creating unable to pickup note:", err);
        }
    }

    /**
     * Post-save function to add part and create authorization.
     * @returns {Promise<void>}
     */
    async function postUpdateAddPartForDeliveryAndAuthorize() {
        const partType = checkForAddPart();
        if (!partType) return;
        const newProduct = buildPartProductItem(partType, chartContext.orderId, mainContext.currentUser);

        await api.send("PUT", "/orders/updateOrderProducts/" + chartContext.orderId, [newProduct]);

        // Extract context
        const { currentUser, productTypes, productSubTypes } = mainContext;
        const orderId = chartContext.orderId;
        const orderObj = chartContext.chart?.orderActivity?.[orderId] ?? {};

        // Get approvalId and approval object
        const orderActivityApprovals = chartContext.chart?.orderActivityApprovals;
        const approvalLink = orderActivityApprovals
            ? Object.values(orderActivityApprovals).find(a => a.orderActivityId === orderId)
            : null;
        const approvalId = approvalLink?.approvalsId;
        const approvalObject = approvalId
            ? chartContext.chart?.approvals?.find(a => a.id === approvalId)
            : null;
        const preAuth = approvalObject?.authNumber?.trim() ?? null;

        // Get products for this order
        const orderActivityProducts = chartContext.chart?.orderActivityProducts;
        const productData = chartContext.chart?.product;
        const orderProducts = orderActivityProducts
            ? Object.values(orderActivityProducts)
                .filter(orderActivityProduct => orderActivityProduct?.orderActivityId === orderId)
                .map(orderActivityProduct => productData?.[orderActivityProduct.productsId])
                .filter(Boolean)
            : [];

        // Build note text
        let noteText = `${currentUser.firstname} ${currentUser.lastname} approved order ${orderId}`;
        noteText += preAuth ? ` With Preauth : ${preAuth} ` : " (Pre-Auth not required) on  ";
        noteText += `${new Date().toLocaleString()} for the following products : `;
        orderProducts.forEach((product) => {
            const productTypeName = product.productTypeId ? (productTypes?.find(type => type.id === product.productTypeId)?.name ?? "") : (product.name ?? "");
            const productSubTypeName = product.productSubTypeId ? (productSubTypes?.find(subType => subType.id === productSubTypeId)?.name ?? "") : (product.itemId ?? "");
            noteText += `${product.qty} ${productTypeName} ${productSubTypeName} | `;
        });
        // Always include the newly added product
        const newProductTypeName = newProduct.productTypeId ? (productTypes?.find(type => type.id === newProduct.productTypeId)?.name ?? "") : (newProduct.name ?? "");
        const newProductSubTypeName = newProduct.productSubTypeId ? (productSubTypes?.find(subType => subType.id === newProduct.productSubTypeId)?.name ?? "") : (newProduct.itemId ?? "");
        noteText += `${newProduct.qty} ${newProductTypeName} ${newProductSubTypeName} | `;

        // Get account and accountId from chartData.account (should only have one entry)
        const accountEntry = chartContext.chart?.account ? Object.values(chartContext.chart.account)[0] : null;
        const note = {
            type: "ActivityNote",
            text: noteText,
            createdAt: new Date().toISOString(),
            accountId: accountEntry?.id,
            task: false,
            completed: true,
            createdById: currentUser.id,
            patientName: orderObj?.patientName,
            activityId: orderObj?.id,
        };
        try {
            await api.send("POST", "/activities/" + orderObj?.id + "/notes", note);
        } catch (err) {
            console.error("Error creating activity note:", err);
        }
    }

    /**
     * Post-save function for approval note.
     * @returns {Promise<void>}
     */
    async function postUpdateAddApprovalNote() {
        const orderId = chartContext.orderId;
        const orderObj = chartContext.chart?.orderActivity?.[orderId] ?? {};
        const orderActivityProducts = chartContext.chart?.orderActivityProducts;
        const productData = chartContext.chart?.product;
        const orderProducts = orderActivityProducts
            ? Object.values(orderActivityProducts)
                .filter(orderActivityProduct => orderActivityProduct?.orderActivityId === orderId)
                .map(orderActivityProduct => productData?.[orderActivityProduct.productsId])
                .filter(Boolean)
            : [];
        // Build note text
        let noteText = `${mainContext.currentUser.firstname} ${mainContext.currentUser.lastname} approved order ${orderId}`;
        noteText += ` on ${new Date().toLocaleString()} for the following products : `;
        orderProducts.forEach((product) => {
            const productTypeName = product.productTypeId ? (mainContext.productTypes?.find(type => type.id === product.productTypeId)?.name ?? "") : (product.name ?? "");
            const productSubTypeName = product.productSubTypeId ? (mainContext.productSubTypes?.find(subType => subType.id === productSubTypeId)?.name ?? "") : (product.itemId ?? "");
            noteText += `${product.qty} ${productTypeName} ${productSubTypeName} | `;
        });
        // Get account and accountId from chartData.account (should only have one entry)
        const accountEntry = chartContext.chart?.account ? Object.values(chartContext.chart.account)[0] : null;
        const note = {
            type: "ApprovalNote",
            text: noteText,
            createdAt: new Date().toISOString(),
            accountId: accountEntry?.id,
            task: false,
            completed: true,
            createdById: mainContext.currentUser.id,
            patientName: orderObj?.patientName,
            activityId: orderObj?.id,
        };
        try {
            await api.send("POST", "/activities/" + orderObj?.id + "/notes", note);
        } catch (err) {
            console.error("Error creating approval note:", err);
        }
    }

    /**
     * Handles all trigger logic before routing.
     * @param {Object} route - Route object.
     * @param {Object} order - Order object.
     * @param {string|null} subType - Selected subtype.
     * @param {string} fromTrigger - Trigger source.
     * @returns {Promise<Object|string|boolean>} Update object, "continue", "stop", or false.
     */
    async function handleRouteTrigger(route, order, subType, fromTrigger) {
        const trigger = route?.trigger;
        return new Promise(async (res, rej) => {
            // Default: clear functionToSendOnRoute
            setFunctionToSendOnRoute(null);
            if (fromTrigger === "fromNote") {
                res("continue");
                return;
            }
            else if (!trigger || fromTrigger) {
                res(false);
            } else if (trigger === "addPartAndAuthorization") {
                const result = await triggerAddPartAndAuthorization();
                res(result);
            } else if (trigger === "addApprovedPart") {
                const result = triggerAddApprovedPart();
                res(result);
            } else if (trigger === "addFollowupDate") {
                const result = await triggerAddFollowupDate();
                res(result);
            } else if (trigger === "needsRedelivery") {
                const result = await triggerHandleNeedsRedelivery();
                res(result);
            } else if (trigger === "addUnableToPickupNote") {
                const result = await triggerAddUnableToPickupNote();
                res(result);
            } else if (trigger === "createPickup") {
                const result = await triggerCreatePickup();
                res(result);
            } else if (trigger === "routeBackToSender") {
                const result = await triggerRouteBackToSender();
                res(result);
            } else if (trigger === "nonBillableCheck") {
                const result = await triggerCheckForNonBillable();
                res(result);
            } else {
                // console.log("Unknown trigger:", trigger, { route, order, subType });
                res(false);
            }
        });
    }

    /**
     * Applies the selected route and subType to the order.
     * Handles triggers before calling chartContext.onChange.
     * @returns {Promise<void>}
     */
    async function handleApplyRoute() {
        if (!selectedRoute) { return; }
        if (selectedRoute.jsonSubTypes?.required && !selectedSubType) { return; }

        const details = getOrderStatusReasonDetailsForRoute(selectedRoute);
        if (details.length > 0 && !selectedOrderStatusReasonDetailId) {
            return;
        }

        try {
            setIsApplyingRoute(true);

            let updates = await handleRouteTrigger(selectedRoute, order, selectedSubType);
            if (updates === "stop") {
                setPendingSaveAfterRoute(true);
                return;
            }

            if (!updates || typeof updates !== "object") {
                updates = {};
            }

            const updateOrderStatusReasonId =
                updates.orderStatusReasonId ||
                selectedRoute.toOrderStatusReasonId ||
                selectedRoute.id;

            const status =
                mainContext.orderStatusReasonsIndexed[updateOrderStatusReasonId].orderStatus;

            updates.orderStatusReasonId = updateOrderStatusReasonId;
            updates.status = status;
            updates.orderStatusReasonDetailId = selectedOrderStatusReasonDetailId;

            const existingServiceOrderReason =
                Object.values(chartContext.chart?.serviceOrderReason || {})
                    .find(reason => reason.currentOrderId === chartContext.orderId);

            let serviceOrderReasonChange = null;

            const activity = chartContext.chart?.activity?.[chartContext.orderId] || null;

            const salesRepUserprofile =
                activity?.ownerId && mainContext.usersIndexed[activity.ownerId]
                    ? mainContext.usersIndexed[activity.ownerId]
                    : null;

            const salesRepName = salesRepUserprofile
                ? `${salesRepUserprofile.firstname} ${salesRepUserprofile.lastname}`
                : "No Sales Rep";

            if (selectedSubType) {
                if (existingServiceOrderReason) {
                    serviceOrderReasonChange = {
                        [existingServiceOrderReason.id]: {
                            id: existingServiceOrderReason.id,
                            serviceReason: selectedSubType,
                            technician: chartContext.chart?.orderActivity?.[chartContext.orderId]?.technician ? chartContext.chart.orderActivity[chartContext.orderId].technician.name : "No Technician",
                            salesRep: salesRepName,
                            atp: chartContext.chart?.orderActivity?.[chartContext.orderId]?.atp ? chartContext.chart.orderActivity[chartContext.orderId].atp.name : "No ATP"
                        }
                    };
                }
            }

            const chartChange = {
                orderActivity: {
                    [chartContext.orderId]: {
                        ...updates
                    }
                }
            };

            if (selectedRoute?.id === "request-business-support-help") {
                const newBusinessSupportRequestId = chartContext.getNegativeId();
                const newRequestNoteId = chartContext.getNegativeId();
                const patient = chartContext.chart?.patient?.[chartContext.patientId] || null;
                const accountId = OrderActivity.getAccount(order, chartContext.chart)?.id ?? patient?.accountId ?? null;
                const requestNoteText = businessSupportRequestNoteText.trim();

                chartChange.note = {
                    [newRequestNoteId]: {
                        id: newRequestNoteId,
                        accountId: accountId,
                        activityId: chartContext.orderId,
                        assignedToId: null,
                        completed: true,
                        createdById: mainContext.currentUser.id,
                        createdAt: new Date().toISOString(),
                        dueDate: null,
                        isAutoVerification: false,
                        patientName: patient ? Patient.getDisplayName(patient) : null,
                        task: false,
                        text: requestNoteText,
                        type: "ActivityNote",
                        noteTypeTermId: 206,
                    }
                };

                chartChange.businessSupportRequest = {
                    [newBusinessSupportRequestId]: {
                        id: newBusinessSupportRequestId,
                        patientId: parseInt(chartContext.patientId),
                        orderActivityId: chartContext.orderId,
                        requestOrderStatusReasonDetailId: selectedOrderStatusReasonDetailId ?? null,
                        businessSupportRequestResolutionTermId: null,
                        requestNoteId: newRequestNoteId,
                        resolutionNoteId: null,
                        resolvedAt: null,
                        resolvedByUserprofileId: null,
                        createdAt: new Date().toISOString(),
                        createdById: mainContext.currentUser.id,
                        deleted: false,
                        deletedAt: null,
                        deletedById: null,
                    }
                };
            }

            if (orderStatusReasonId === 116 && !manualRouteMode) {
                const latestBusinessSupportRequest = getLatestBusinessSupportRequestForCurrentOrder();
                if (!latestBusinessSupportRequest) {
                    console.error("Unable to resolve Business Support Request: no existing request found for this order.");
                    abortRouting();
                    return;
                }

                const newResolutionNoteId = chartContext.getNegativeId();
                const patient = chartContext.chart?.patient?.[chartContext.patientId] || null;
                const accountId = OrderActivity.getAccount(order, chartContext.chart)?.id ?? patient?.accountId ?? null;

                chartChange.note = {
                    ...(chartChange.note || {}),
                    [newResolutionNoteId]: {
                        id: newResolutionNoteId,
                        accountId: accountId,
                        activityId: chartContext.orderId,
                        assignedToId: null,
                        completed: true,
                        createdById: mainContext.currentUser.id,
                        createdAt: new Date().toISOString(),
                        dueDate: null,
                        isAutoVerification: false,
                        patientName: patient ? Patient.getDisplayName(patient) : null,
                        task: false,
                        text: businessSupportResolutionNoteText.trim(),
                        type: "ActivityNote",
                        noteTypeTermId: 206,
                    }
                };

                chartChange.businessSupportRequest = {
                    ...(chartChange.businessSupportRequest || {}),
                    [latestBusinessSupportRequest.id]: {
                        id: latestBusinessSupportRequest.id,
                        requestOrderStatusReasonDetailId: selectedBusinessSupportRequestReasonDetailId ?? null,
                        businessSupportRequestResolutionTermId: selectedBusinessSupportResolutionTermId,
                        resolutionNoteId: newResolutionNoteId,
                        resolvedAt: new Date().toISOString(),
                        resolvedByUserprofileId: mainContext.currentUser.id,
                    }
                };
            }

            if (serviceOrderReasonChange) {
                chartChange.serviceOrderReason = serviceOrderReasonChange;
            }

            chartContext.onChange(chartChange);

            setPendingSaveAfterRoute(true);

        } catch (error) {
            console.log("apply route not wired or failed:", {
                orderId: chartContext.orderId,
                route: selectedRoute,
                subType: selectedSubType,
                error: error,
            });
            setIsApplyingRoute(false);
        }
    }

    /**
     * Create-ticket flow triggers routing indirectly by setting a context flag.
     * This watcher runs the same apply-route logic used by the Route button.
     */
    useEffect(() => {
        if (chartContext.pendingCreateTicket) {
            // handleApplyRoute is async, so we need an async IIFE to await it
            (async () => {
                try {
                    // Trigger full routing flow (includes triggers, updates, and save)
                    await handleApplyRoute();
                } catch (err) {
                    console.error("Error running handleApplyRoute via pendingCreateTicket:", err);
                } finally {
                    // Reset the flag to prevent loops
                    chartContext.setPendingCreateTicket(false);
                }
            })();
        }
    }, [chartContext.pendingCreateTicket]);

    /**
     * Roll back routing-only fields when rule or validation issue is detected
     */
    useEffect(() => {
        const hasRuleFailures = Object.values(chartContext.ruleViolations || {}).some(
            ruleResult => ruleResult && ruleResult.passed === false
        );
        const hasValidationIssues = Object.keys(chartContext.validationIssues || {}).length > 0;

        if (hasRuleFailures || (pendingSaveAfterRoute && hasValidationIssues)) {
            chartContext.onChange({
                orderActivity: {
                    [chartContext.orderId]: {
                        orderStatusReasonId: chartContext.originalChart?.orderActivity?.[chartContext.orderId]?.orderStatusReasonId ?? null,
                        orderStatusReasonDetailId: chartContext.originalChart?.orderActivity?.[chartContext.orderId]?.orderStatusReasonDetailId ?? null,
                        status: chartContext.originalChart?.orderActivity?.[chartContext.orderId]?.status ?? null,
                    }
                }
            });
            abortRouting();
        }
    }, [chartContext.ruleViolations, chartContext.validationIssues, pendingSaveAfterRoute]);

    /**
     * After route changes are staged in chart state, this watcher performs the
     * actual save and runs any deferred post-route callback.
     */
    useEffect(() => {
        if (pendingSaveAfterRoute && !saveAfterRouteRef.current) {
            saveAfterRouteRef.current = true;
            (async () => {
                await chartContext.handleSaveChart(functionToSendOnRoute);

                if (functionToRunAfterRouteRef.current) {
                    try {
                        await functionToRunAfterRouteRef.current();
                    } catch (err) {
                        console.error("Error running functionToRunAfterRoute:", err);
                    }
                    functionToRunAfterRouteRef.current = null;
                }

                saveAfterRouteRef.current = false;
            })();
        }
    }, [pendingSaveAfterRoute]);

    function getGridItemProps() {
        return {
            xs: 12 / 1,
            sm: 12 / 2,
            md: 12 / 3,
            lg: 12 / 4,
            sx: { display: "flex" }
        };
    }

    // Helper to get orderStatusReasonDetails for a route
    function getOrderStatusReasonDetailsForRoute(route) {
        if (!route || !mainContext.orderStatusReasonDetailsIndexed) {
            return [];
        }
        return OrderStatusReasonDetail.sort(Object.values(mainContext.orderStatusReasonDetailsIndexed).filter(
            orderStatusReasonDetail => orderStatusReasonDetail.orderStatusReasonId === route.toOrderStatusReasonId && !orderStatusReasonDetail.deleted
        ));
    }

    // Helper to get orderStatusReasonDetails for a reason ID
    function getOrderStatusReasonDetailsForReasonId(orderStatusReasonId) {
        if (!orderStatusReasonId || !mainContext.orderStatusReasonDetailsIndexed) {
            return [];
        }

        return Object.values(mainContext.orderStatusReasonDetailsIndexed).filter(
            detail =>
                detail.orderStatusReasonId === orderStatusReasonId
        );
    }

    function renderOrderStatusReasonDetails(route) {
        const details = getOrderStatusReasonDetailsForRoute(route);

        if (!route || !selectedRoute || selectedRoute.id !== route.id || details.length === 0) {
            return null;
        }

        return (
            <MUIBox sx={{ flexGrow: 1, width: "100%"}}>
                <MUITypography variant="subtitle2">Detail*</MUITypography>
                <MUIGrid container spacing={2}>
                    {details.map((detail) => (
                        <MUIGrid
                            item
                            key={detail.id}
                            {...getGridItemProps()}
                        >
                            <ChipQueueOrderStatusReasonDetail
                                label={OrderStatusReasonDetail.getDisplayName(detail)}
                                value={detail}
                                orderStatusReasonDetail={detail}
                                selected={selectedOrderStatusReasonDetailId === detail.id}
                                selectable={true}
                                clickable={true}
                                fullWidth={true}
                                onClick={() => {
                                    setSelectedOrderStatusReasonDetailId(selectedOrderStatusReasonDetailId === detail.id ? null : detail.id)
                                }}
                            />
                        </MUIGrid>
                    ))}
                </MUIGrid>
            </MUIBox>
        );
    }



    /**
     * Renders the manual route input (autocomplete) for manual routing mode.
     * @returns {JSX.Element}
     */
    function renderManualRouteInput() {
        const orderStatusesIndexed = mainContext?.orderStatusesIndexed ?? {};
        const allOrderStatusReasons = Object.values(mainContext.orderStatusReasonsIndexed || {})
            .filter(reason => reason.orderStatus !== 4);

        const sortedOrderStatusReasons = [...allOrderStatusReasons].sort((a, b) => {
            if (a.orderStatus !== b.orderStatus) {
                return a.orderStatus - b.orderStatus;
            }
            return (a.description || "").localeCompare(b.description || "");
        });

        return (
            <MUIBox>
                <MUIGrid container spacing={2}>
                    <MUIGrid item xs={12} md={6}>
                        <MUIAutocomplete
                            options={sortedOrderStatusReasons}
                            getOptionLabel={(orderStatusReason) =>
                                `${OrderStatus.getDisplayName(
                                    orderStatusesIndexed[orderStatusReason.orderStatus]
                                )} • ${OrderStatusReason.getDisplayName(orderStatusReason)}`
                            }
                            value={selectedRoute}
                            onChange={(_, newValue) => {
                                setSelectedRoute(newValue);
                                setSelectedSubType(null);
                                setSelectedOrderStatusReasonDetailId(null);
                            }}
                            renderInput={(params) => (
                                <MUITextField
                                    {...params}
                                    label="Destination"
                                    variant="standard"
                                    size="small"
                                    fullWidth
                                />
                            )}
                            isOptionEqualToValue={(option, value) => {
                                return option.id === value?.id;
                            }}
                        />
                    </MUIGrid>
                </MUIGrid>

                {selectedRoute && (() => {
                    const details = getOrderStatusReasonDetailsForReasonId(selectedRoute.id);

                    if (details.length === 0) {
                        return null;
                    }

                    return (
                        <MUIGrid container spacing={2} sx={{ mt: 1 }}>
                            <MUIGrid item xs={12} md={6}>
                                <MUIAutocomplete
                                    options={details}
                                    value={
                                        details.find(
                                            detail => detail.id === selectedOrderStatusReasonDetailId
                                        ) ?? null
                                    }
                                    getOptionLabel={(detail) =>
                                        OrderStatusReasonDetail.getDisplayName(detail)
                                    }
                                    onChange={(_, newValue) => {
                                        setSelectedOrderStatusReasonDetailId(newValue?.id ?? null);
                                    }}
                                    renderInput={(params) => (
                                        <MUITextField
                                            {...params}
                                            label="Detail"
                                            variant="standard"
                                            size="small"
                                            fullWidth
                                            required
                                        />
                                    )}
                                    isOptionEqualToValue={(option, value) => {
                                        return option.id === value?.id;
                                    }}
                                />
                            </MUIGrid>
                        </MUIGrid>
                    );
                })()}

                <MUIAlert severity="warning" sx={{ mt: 2 }}>
                    All limitations and protections are off. Please use caution when manually routing.
                </MUIAlert>
            </MUIBox>
        );
    }

    /**
     * Keeps create-ticket "routing required" validation in sync with current
     * route/subtype selection state.
     */
    useEffect(() => {
        if (props?.requiredValidation && selectedRoute) {
            const subtypeOptions = selectedRoute.jsonSubTypes?.options || [];
            if (subtypeOptions.length === 0) {
                chartContext.setCreateTicketValidationRules({ ...chartContext.createTicketValidationRules, routing: true });
            } else {
                if (selectedSubType) {
                    chartContext.setCreateTicketValidationRules({ ...chartContext.createTicketValidationRules, routing: true });
                } else {
                    // Remove the routing validation rule if a subtype is disslected (null)
                    const currentValidationRules = chartContext.createTicketValidationRules;
                    const { routing, ...restOfValidationRules } = currentValidationRules;
                    chartContext.setCreateTicketValidationRules(restOfValidationRules);
                }
            }
        } else if (props?.requiredValidation && !selectedRoute) {
            // Remove the routing validation rule if a route is disslected (null)
            const currentValidationRules = chartContext.createTicketValidationRules;
            const { routing, ...restOfValidationRules } = currentValidationRules;
            chartContext.setCreateTicketValidationRules(restOfValidationRules);
        }
    }, [selectedRoute, selectedSubType]);

    /**
     * Renders the grid of available route options for normal routing mode.
     * @returns {JSX.Element}
     */
    function renderNormalRouteGrid() {
        const orderStatusesIndexed = mainContext?.orderStatusesIndexed ?? {};
        const groupedRoutesKeys = Object.keys(availableRoutesGrouped);
        const hardcodedBusinessSupportRoute = {
            id: "request-business-support-help",
            name: "Business Support",
            toOrderStatusReasonId: 116,
            trigger: null,
            jsonSubTypes: null,
        };
        const hardcodedBusinessSupportItem = {
            queueRoute: hardcodedBusinessSupportRoute,
            orderStatusId: 1,
            statusName: orderStatusesIndexed[1]?.name ?? "Status 1",
        };

        // Flatten and sort routes by orderStatusId (number), then route description
        const flatQueueRoutes = [];
        groupedRoutesKeys.forEach(orderStatusId => {
            const routes = availableRoutesGrouped[orderStatusId];
            routes.forEach((queueRoute) => {
                // Handling deprecated triggers this way (just removing the trigger from the routes)
                // so that we do not have to handle deprecated triggers in multiple places,
                // like the trigger handler and the trigger information display area
                const normalizedQueueRoute = {
                    ...queueRoute,
                    trigger: deprecatedRouteTriggers.includes(queueRoute.trigger)
                        ? null
                        : queueRoute.trigger
                };

                flatQueueRoutes.push({
                    queueRoute: normalizedQueueRoute,
                    orderStatusId: Number(orderStatusId),
                    statusName: orderStatusesIndexed[orderStatusId]?.name ?? `Status ${orderStatusId}`,
                });
            });
        });

        flatQueueRoutes.sort((a, b) => {
            if (a.orderStatusId !== b.orderStatusId) {
                return a.orderStatusId - b.orderStatusId;
            }
            return (a.queueRoute.name || "").localeCompare(b.queueRoute.name || "");
        });

        const shouldAddBusinessSupportRoute =
            order?.status !== 4 &&
            ![116, 121, 0, null, undefined].includes(orderStatusReasonId);
        //116 = Business support, don't want to show it while already there.
        //121 = The new "fake" reasonId for the create billing issue pickup ticket flow fro mthe createPickupTrigger
        //0, null, undefined are to make sure we don't display this when creating a new ticket or don't actually have a ticket/order loaded  -MB
        if (shouldAddBusinessSupportRoute) {
            flatQueueRoutes.push(hardcodedBusinessSupportItem);
        }

        if (flatQueueRoutes.length === 0) {
            return (
                <EmptyState
                    line1="No available routes"
                    line2="Contact support if you believe this is an issue"
                />
            );
        }

        return (
            <MUIStack direction="column" gap={2}>
                <MUIGrid container spacing={2}>
                    {flatQueueRoutes.map((item) => (
                        <MUIGrid
                            item
                            key={item.queueRoute.id}
                            {...getGridItemProps()}
                        >
                            <ChipQueueRoute
                                queueRoute={item.queueRoute}
                                selectable={true}
                                clickable={true}
                                fullWidth={true}
                                selected={selectedRoute?.id === item.queueRoute.id}
                                onClick={() => {
                                    if (selectedRoute && selectedRoute.id === item.queueRoute.id) {
                                        setSelectedRoute(null);
                                        setSelectedSubType(null);
                                        setSelectedOrderStatusReasonDetailId(null);
                                    } else {
                                        setSelectedRoute(item.queueRoute);
                                        setSelectedSubType(null);
                                        setSelectedOrderStatusReasonDetailId(null);
                                    }
                                }}
                            />
                        </MUIGrid>
                    ))}
                </MUIGrid>
                {props?.requiredValidation &&
                    chartContext.createTicketValidationIssues.routing &&
                    (selectedRoute?.jsonSubTypes?.options?.length === 0 || !selectedRoute?.jsonSubTypes?.options) ? (
                    <MUITypography variant="caption" color="error" sx={{ display: "block" }}>Required</MUITypography>
                ) : null}
                {selectedRoute && renderOrderStatusReasonDetails(selectedRoute)}
                {selectedRoute && renderSubTypeOptions(selectedRoute)}
                {selectedRoute && renderBusinessSupportRequestInput(selectedRoute)}
                {selectedRoute && renderBusinessSupportResolutionInputs(selectedRoute)}
                {selectedRoute && selectedRoute.trigger && triggerDescription && (
                    <MUIAlert severity="info">
                        {triggerDescription}
                    </MUIAlert>
                )}
            </MUIStack>
        );
    }



    return (
        <Card
            renderContent={renderContent}
            getActions={getActions}
            getTitle={() => title}
            getIcon={() => icon}
        />
    );
}
CardChartRouting.meta = { className: className, title: title, icon: icon };
